import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useJoin } from './joinContext';
import { getCurrencyMeta } from '../../utils/currency';
import { buildJoinBillingPreview } from './billingPreview';
import { getMembershipHandling, isClubManagedMembershipMode } from '../../utils/membershipHandling';
import '../../styles/joinFlow.css';

const PRICING_QUESTION_IDS = ['pricingTags', 'pricingTag'];

function isPricingQuestion(question) {
  return PRICING_QUESTION_IDS.includes(question?.id);
}

function formatMinor(minor, currency = 'GBP') {
  const { symbol, decimals } = getCurrencyMeta(currency);
  const major = Number(minor || 0) / Math.pow(10, decimals);
  return `${symbol}${major.toFixed(decimals)}`;
}

function buildTargetLabel({
  user,
  membershipPlanType,
  membershipTargetCount,
  membershipTargetUserIds,
  familyMembershipGroups,
  selectedFamilyId,
  selectedIndividualTargetFamilyId,
  selectedIndividualTargetUserId,
}) {
  const targetIds = Array.isArray(membershipTargetUserIds) ? membershipTargetUserIds : [];
  const groups = Array.isArray(familyMembershipGroups) ? familyMembershipGroups : [];
  const allMembers = groups.flatMap((group) => (
    Array.isArray(group.members)
      ? group.members.map((member) => ({ ...member, familyName: group.familyName, familyId: group.familyId }))
      : []
  ));
  const nameFor = (uid) => {
    if (!uid) return '';
    if (uid === user?.uid) return 'You';
    const match = allMembers.find((member) => member.uid === uid);
    return match?.name || 'Family member';
  };

  if (membershipPlanType === 'family_tier') {
    const familyName = groups.find((group) => group.familyId === selectedFamilyId)?.familyName || '';
    const names = targetIds.map(nameFor).filter(Boolean);
    if (names.length > 0 && names.length <= 3) {
      return `${names.join(', ')}${familyName ? ` (${familyName})` : ''}`;
    }
    return `${Math.max(1, membershipTargetCount || targetIds.length || 1)} members${familyName ? ` (${familyName})` : ''}`;
  }

  const targetUid = selectedIndividualTargetUserId || targetIds[0] || user?.uid || '';
  const targetName = nameFor(targetUid);
  const familyName = selectedIndividualTargetFamilyId
    ? groups.find((group) => group.familyId === selectedIndividualTargetFamilyId)?.familyName
    : '';
  return `${targetName || 'You'}${familyName && targetUid !== user?.uid ? ` (${familyName})` : ''}`;
}

function getCurrentStepKey(status, courseSignupRequested) {
  if (courseSignupRequested) {
    if (status === 'joined') return 'joined';
    if (status === 'payment') return 'payment';
    return 'details';
  }
  if (status === 'membership_type') return 'membership';
  if (status === 'form') return 'details';
  if (status === 'waiting') return 'approval';
  if (status === 'waitlisted') return 'waitlist';
  if (status === 'payment') return 'payment';
  if (status === 'joined') return 'joined';
  return 'membership';
}

function JoinProgress({ steps, currentKey }) {
  const matchedIndex = steps.findIndex((step) => step.key === currentKey);
  const currentIndex = currentKey === 'joined'
    ? Math.max(steps.length - 1, 0)
    : Math.max(0, matchedIndex);
  const currentStep = steps[currentIndex] || steps[0] || { label: 'Join' };
  const progressPct = steps.length > 0 ? Math.round(((currentIndex + 1) / steps.length) * 100) : 0;

  return (
    <>
      <nav className="join-progress" aria-label="Join progress">
        {steps.map((step, index) => {
          const isActive = step.key === currentKey;
          const isDone = currentKey === 'joined' || index < currentIndex;
          return (
            <div
              key={step.key}
              className={`join-progress__item${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="join-progress__marker">{isDone ? 'Done' : index + 1}</span>
              <span className="join-progress__label">{step.label}</span>
            </div>
          );
        })}
      </nav>
      <div className="join-progress-mobile" aria-label="Join progress">
        <div className="join-progress-mobile__meta">
          <span>Step {currentIndex + 1} of {Math.max(steps.length, 1)}</span>
          <strong>{currentStep.label}</strong>
        </div>
        <div className="join-progress-mobile__bar" aria-hidden="true">
          <span style={{ width: `${progressPct}%` }} />
        </div>
      </div>
    </>
  );
}

function SummaryRow({ label, value, emphasis = false }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className={`join-summary__row${emphasis ? ' join-summary__row--emphasis' : ''}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function JoinSummaryPanel({ learnToRowRequested, learnToSailRequested, canEditMembership }) {
  const {
    status,
    setStatus,
    team,
    user,
    stripeSetup,
    stripeFeeBreakdown: contextFeeBreakdown,
    joinPaymentSummary,
    cadenceMode,
    cadenceOptions,
    selectedCadence,
    membershipPlanType,
    selectedFamilyTier,
    membershipTargetCount,
    membershipTargetUserIds,
    membershipPricingTag,
    membershipDiscountCode,
    preassignedTags,
    familyMembershipGroups,
    selectedFamilyId,
    selectedIndividualTargetFamilyId,
    selectedIndividualTargetUserId,
    waitlistEnabled,
    requiredJoinDocuments,
    pendingMemberStatus,
    isPrelaunchCommitteeMode: prelaunchCommittee,
  } = useJoin();

  const currency = stripeSetup?.currency || 'GBP';
  const stripeFeeBreakdown = joinPaymentSummary?.breakdown
    ? { ...contextFeeBreakdown, ...joinPaymentSummary.breakdown }
    : contextFeeBreakdown;
  const awaitingCadenceChoice = cadenceMode === 'member_choice'
    && (status === 'membership_type' || status === 'form' || !selectedCadence);
  const awaitingOfflinePayment = pendingMemberStatus === 'awaiting_club_payment';
  const clubManaged = awaitingOfflinePayment || joinPaymentSummary?.route === 'offline' || isClubManagedMembershipMode(getMembershipHandling(team || {}).mode);
  const externalDisplayMode = stripeSetup?.externalPaymentDisplayMode || 'instructions_only';
  const externalShowsPrice = clubManaged && (externalDisplayMode === 'price_only' || externalDisplayMode === 'price_and_instructions');
  const membershipYearInfo = stripeFeeBreakdown?.membershipYear || {};
  const membershipYearApplied = !awaitingCadenceChoice && (joinPaymentSummary?.renewalSetup || membershipYearInfo?.applied === true);
  const renewalFinalAmount = Number(membershipYearInfo?.fullCycle?.finalAmount || 0);
  const renewalDateLabel = joinPaymentSummary?.renewalAt || membershipYearInfo?.nextRenewalAt
    ? new Date(joinPaymentSummary?.renewalAt || membershipYearInfo.nextRenewalAt).toLocaleDateString('en-GB')
    : '';
  const planLabel = stripeFeeBreakdown?.membershipPlanLabel
    || (membershipPlanType === 'family_tier' ? selectedFamilyTier?.label : 'Individual Membership');
  const normalizedPreassignedTags = useMemo(() => (
    Array.isArray(preassignedTags)
      ? preassignedTags.map((tag) => (typeof tag === 'string' ? tag.trim() : '')).filter(Boolean)
      : []
  ), [preassignedTags]);
  const billingPreview = useMemo(() => buildJoinBillingPreview({
    stripeSetup,
    cadenceMode,
    cadenceOptions,
    selectedCadence,
    showSelectedMemberChoice: status === 'payment',
    membershipPlanType,
    selectedFamilyTier,
    memberCount: membershipTargetCount,
    appliedTags: [
      ...normalizedPreassignedTags,
      ...(membershipPricingTag ? [membershipPricingTag] : [])
    ],
    preferredTag: membershipPricingTag || '',
    externalPayment: clubManaged,
  }), [
    cadenceMode,
    cadenceOptions,
    membershipPlanType,
    membershipPricingTag,
    membershipTargetCount,
    normalizedPreassignedTags,
    selectedCadence,
    selectedFamilyTier,
    status,
    stripeSetup,
    clubManaged,
  ]);
  const cadenceLabel = awaitingCadenceChoice ? 'Choose monthly or yearly at payment' : clubManaged
    ? selectedCadence?.unit === 'month' ? 'Monthly' : 'Yearly'
    : billingPreview?.summaryLabel || '';
  const targetLabel = buildTargetLabel({
    user,
    membershipPlanType,
    membershipTargetCount,
    membershipTargetUserIds,
    familyMembershipGroups,
    selectedFamilyId,
    selectedIndividualTargetFamilyId,
    selectedIndividualTargetUserId,
  });
  const finalAmount = awaitingCadenceChoice ? 'Choose billing frequency first' : joinPaymentSummary?.renewalSetup ? formatMinor(0, currency) : stripeFeeBreakdown ? formatMinor(stripeFeeBreakdown.finalAmount, currency) : '';
  const externalPrice = externalShowsPrice && Number(stripeFeeBreakdown?.baseAmount || 0) > 0
    ? `${formatMinor(stripeFeeBreakdown.baseAmount, currency)}${membershipYearApplied ? ' for this membership period' : selectedCadence?.unit === 'month' ? '/month' : '/year'}`
    : '';
  const courseSignupRequested = learnToRowRequested || learnToSailRequested;
  const courseLabel = learnToSailRequested ? 'Learn to Sail' : 'Learn to Row';
  const nextAction = useMemo(() => {
    if (courseSignupRequested && status === 'joined') return `Your ${courseLabel} place is confirmed.`;
    if (courseSignupRequested && status === 'payment') return 'Payment has returned from Stripe. The course place is being confirmed.';
    if (courseSignupRequested) return 'Choose a course, confirm details, then continue to payment or the course waitlist.';
    if (status === 'membership_type') return 'Choose the membership and who it applies to.';
    if (status === 'form') return 'Complete any required details and documents.';
    if (prelaunchCommittee && status === 'payment') return 'Prepare membership payment for launch. No online payment is taken today.';
    if (prelaunchCommittee && status === 'joined') return 'Membership setup is complete. Membership starts when the club goes live.';
    if (status === 'payment') return clubManaged
      ? 'Review the club’s payment information and finish joining.'
      : 'Review the cost and choose how to pay.';
    if (status === 'waiting') return awaitingOfflinePayment ? 'Arrange payment with the club and wait for an administrator to confirm it.' : 'An admin needs to review this request before payment opens.';
    if (status === 'waitlisted') return 'Payment opens when an admin releases a place.';
    if (status === 'joined') return 'Membership is active.';
    return 'Continue through the join steps.';
  }, [awaitingOfflinePayment, clubManaged, courseLabel, courseSignupRequested, prelaunchCommittee, status]);
  const statusLabel = courseSignupRequested && status === 'joined'
    ? 'Confirmed'
    : courseSignupRequested && status === 'payment'
      ? 'Payment'
      : courseSignupRequested
        ? courseLabel
        : status === 'membership_type'
      ? 'Membership'
      : status === 'form'
        ? 'Details'
        : status === 'payment'
          ? clubManaged ? 'Club payment' : 'Payment'
          : status === 'waiting'
            ? awaitingOfflinePayment ? 'Awaiting payment confirmation' : 'Awaiting approval'
            : status === 'waitlisted'
              ? 'Waitlisted'
              : status === 'joined'
                ? 'Joined'
                : 'Joining';
  const secondaryDetails = (
    <>
      <SummaryRow label="Club" value={team?.name || 'Club'} />
      {courseSignupRequested ? (
        <SummaryRow label="Path" value={`${courseLabel} signup`} />
      ) : (
        <>
          {membershipPlanType !== 'family_tier' && (
            <SummaryRow label="Pricing" value={membershipPricingTag || 'Default pricing'} />
          )}
          {(!clubManaged || externalShowsPrice) && <SummaryRow label="Billing" value={cadenceLabel} />}
        </>
      )}
      {!clubManaged && membershipYearApplied && (
        <SummaryRow
          label="Renews"
          value={renewalFinalAmount > 0
            ? `${formatMinor(renewalFinalAmount, currency)}${renewalDateLabel ? ` on ${renewalDateLabel}` : ''}`
            : renewalDateLabel}
        />
      )}
      {!clubManaged && membershipDiscountCode?.trim() && (
        <SummaryRow label="Discount" value={membershipDiscountCode.trim().toUpperCase()} />
      )}
      {Array.isArray(requiredJoinDocuments) && requiredJoinDocuments.length > 0 && (
        <SummaryRow label="Documents" value={`${requiredJoinDocuments.length} required`} />
      )}
      {!courseSignupRequested && waitlistEnabled && (
        <SummaryRow label="Waitlist" value="Payment follows admin release" />
      )}
    </>
  );
  const feeDetails = !joinPaymentSummary?.renewalSetup && !awaitingCadenceChoice && !courseSignupRequested && !clubManaged && stripeFeeBreakdown ? (
    <dl>
      <SummaryRow label={prelaunchCommittee ? 'Base at launch' : membershipYearApplied ? 'Base today' : 'Base'} value={formatMinor(stripeFeeBreakdown.baseAmount, currency)} />
      {Number(stripeFeeBreakdown.platformFee || 0) > 0 && (
        <SummaryRow label={prelaunchCommittee ? 'Platform at launch' : membershipYearApplied ? 'Platform today' : 'Platform'} value={formatMinor(stripeFeeBreakdown.platformFee, currency)} />
      )}
      {Number(stripeFeeBreakdown.stripeFee || 0) > 0 && (
        <SummaryRow label={prelaunchCommittee ? 'Stripe at launch' : membershipYearApplied ? 'Stripe today' : 'Stripe'} value={formatMinor(stripeFeeBreakdown.stripeFee, currency)} />
      )}
    </dl>
  ) : null;

  return (
    <aside className="join-summary" aria-label="Join summary">
      <div className="join-summary__header">
        <span className="join-summary__eyebrow">Current step</span>
        <strong>{statusLabel}</strong>
        <p>{nextAction}</p>
      </div>

      <dl className="join-summary__list join-summary__list--desktop">
        <SummaryRow label="Club" value={team?.name || 'Club'} />
        {courseSignupRequested ? (
          <SummaryRow label="Path" value={`${courseLabel} signup`} />
        ) : (
          <>
            <SummaryRow label="Membership" value={planLabel} />
            <SummaryRow
              label="For"
              value={targetLabel}
            />
            {membershipPlanType !== 'family_tier' && (
              <SummaryRow label="Pricing" value={membershipPricingTag || 'Default pricing'} />
            )}
            {(!clubManaged || externalShowsPrice) && <SummaryRow label="Billing" value={cadenceLabel} />}
            {clubManaged ? (
              externalPrice && <SummaryRow label="Club-stated fee" value={externalPrice} emphasis />
            ) : (
              <SummaryRow
                label={prelaunchCommittee ? 'Estimated at launch' : membershipYearApplied ? 'Today' : 'Estimated total'}
                value={finalAmount || 'Calculating'}
                emphasis
              />
            )}
          </>
        )}
        {!clubManaged && membershipYearApplied && (
          <SummaryRow
            label="Renews"
            value={renewalFinalAmount > 0
              ? `${formatMinor(renewalFinalAmount, currency)}${renewalDateLabel ? ` on ${renewalDateLabel}` : ''}`
              : renewalDateLabel}
          />
        )}
        {!clubManaged && membershipDiscountCode?.trim() && (
          <SummaryRow label="Discount" value={membershipDiscountCode.trim().toUpperCase()} />
        )}
        {Array.isArray(requiredJoinDocuments) && requiredJoinDocuments.length > 0 && (
          <SummaryRow label="Documents" value={`${requiredJoinDocuments.length} required`} />
        )}
        {!courseSignupRequested && waitlistEnabled && (
          <SummaryRow label="Waitlist" value="Payment follows admin release" />
        )}
      </dl>

      <dl className="join-summary__list join-summary__list--mobile">
        {!courseSignupRequested && (
          <>
            <SummaryRow label="Membership" value={planLabel} />
            <SummaryRow label="For" value={targetLabel} />
            {clubManaged ? (
              externalPrice && <SummaryRow label="Club-stated fee" value={externalPrice} emphasis />
            ) : (
              <SummaryRow
                label={prelaunchCommittee ? 'Estimated at launch' : membershipYearApplied ? 'Today' : 'Estimated total'}
                value={finalAmount || 'Calculating'}
                emphasis
              />
            )}
          </>
        )}
      </dl>

      <details className="join-summary__mobile-details">
        <summary>More details</summary>
        <dl>
          {secondaryDetails}
        </dl>
        {feeDetails && (
          <div className="join-summary__mobile-fees">
            <strong>Fee breakdown</strong>
            {feeDetails}
          </div>
        )}
      </details>

      {!joinPaymentSummary?.renewalSetup && !awaitingCadenceChoice && !courseSignupRequested && !clubManaged && stripeFeeBreakdown && (
        <details className="join-summary__fees join-summary__fees--desktop">
          <summary>Fee breakdown</summary>
          {feeDetails}
        </details>
      )}

      {canEditMembership && !joinPaymentSummary?.renewalSetup && status !== 'membership_type' && status !== 'waiting' && status !== 'waitlisted' && status !== 'joined' && (
        <button type="button" className="join-summary__edit" onClick={() => setStatus('membership_type')}>
          Edit membership
        </button>
      )}
    </aside>
  );
}

export default function JoinFlowShell({
  children,
  learnToRowRequested = false,
  learnToSailRequested = false,
  isAdminDemo = false,
  isAdmin = false,
  isAdminManagingMembership = false,
}) {
  const {
    status,
    team,
    teamId,
    questions,
    requiredJoinDocuments,
    waitlistEnabled,
    pendingMemberStatus,
    joinPaymentSummary,
  } = useJoin();
  const awaitingOfflinePayment = pendingMemberStatus === 'awaiting_club_payment';
  const courseSignupRequested = learnToRowRequested || learnToSailRequested;
  const clubManaged = !courseSignupRequested
    && isClubManagedMembershipMode(getMembershipHandling(team || {}).mode);
  const courseLabel = learnToSailRequested ? 'Learn to Sail' : 'Learn to Row';
  const hasJoinFormQuestions = Array.isArray(questions)
    && questions.some((question) => !isPricingQuestion(question));
  const hasRequiredJoinDocuments = Array.isArray(requiredJoinDocuments) && requiredJoinDocuments.length > 0;
  const requiresApproval = team?.availability?.joinable === 'On Approval';
  const requiresDetailsStep = courseSignupRequested || requiresApproval || hasJoinFormQuestions || hasRequiredJoinDocuments;
  const moderationStep = waitlistEnabled || status === 'waitlisted'
    ? { key: 'waitlist', label: 'Waitlist' }
    : requiresApproval || status === 'waiting'
      ? { key: 'approval', label: awaitingOfflinePayment ? 'Payment confirmation' : 'Approval' }
      : null;
  const steps = courseSignupRequested
    ? [
      { key: 'details', label: 'Course' },
      { key: 'payment', label: 'Payment' },
    ]
    : [
      { key: 'membership', label: 'Membership' },
      ...(requiresDetailsStep ? [{ key: 'details', label: 'Details' }] : []),
      ...(moderationStep ? [moderationStep] : []),
      ...(!awaitingOfflinePayment ? [{ key: 'payment', label: clubManaged ? 'Club payment' : 'Payment' }] : []),
    ];
  const currentStep = getCurrentStepKey(status, courseSignupRequested);
  const title = joinPaymentSummary?.renewalSetup ? `Set up online renewals for ${team?.name || 'Club'}` : courseSignupRequested ? `${team?.name || 'Club'} ${courseLabel}` : `Join ${team?.name || 'Club'}`;
  const intro = joinPaymentSummary?.renewalSetup
    ? 'Save a card for future renewals. Your existing paid membership stays active.'
    : courseSignupRequested
    ? 'Choose the course and account, then complete any required details before payment or waitlist release.'
    : status === 'payment'
      ? clubManaged
        ? 'Review the club’s external payment information and finish joining.'
        : 'Review your membership cost and choose how you want to pay.'
      : status === 'form'
        ? 'Complete the details the club needs before payment or review.'
        : status === 'waiting'
          ? awaitingOfflinePayment ? 'Arrange payment with the club, then wait for payment confirmation.' : 'Your request is with the club for review.'
          : status === 'waitlisted'
            ? 'Your place is being managed by the club before payment opens.'
            : 'Choose the membership and account before continuing.';

  return (
    <section className="join-flow-shell">
      <header className="join-flow-shell__header">
        <div>
          <p className="join-flow-shell__kicker">{courseSignupRequested ? 'Course signup' : 'Membership signup'}</p>
          <h1>{title}</h1>
          <p>{intro}</p>
        </div>
        {isAdminDemo && (
          <Link className="join-flow-shell__admin-link" to={`/teams/${teamId}`}>
            Exit demo
          </Link>
        )}
      </header>

      {(isAdminDemo || (!isAdminDemo && isAdmin)) && (
        <div className="join-flow-shell__notice">
          {isAdminDemo
            ? 'Demo mode is on for admins. This preview is read-only and will not submit a join request.'
            : isAdminManagingMembership
              ? 'You are reviewing your own membership setup as an admin.'
              : 'You are an admin. You can use this flow to set up or review your own membership.'}
        </div>
      )}

      <JoinProgress steps={steps} currentKey={currentStep} />

      <div className="join-flow-shell__body">
        <main className="join-flow-shell__main">
          {children}
        </main>
        <JoinSummaryPanel
          learnToRowRequested={learnToRowRequested}
          learnToSailRequested={learnToSailRequested}
          canEditMembership={!courseSignupRequested}
        />
      </div>
    </section>
  );
}
